import { useState, useEffect } from "react";
import { User, PenTool, Lock, Save, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import SignaturePad from "../../components/SignaturePad";

export default function VetSettings() {
  const [me, setMe] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [profile, setProfile] = useState({
    first_name: "", last_name: "", phone: "", license_number: "",
    address_line1: "", address_line2: "", city: "", province: "", postal_code: "",
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current: "", new_pw: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [sig, setSig] = useState("");
  const [savingSig, setSavingSig] = useState(false);

  useEffect(() => {
    api.get("/vet/me").then(r => {
      setMe(r.data);
      setProfile({
        first_name: r.data.first_name || "", last_name: r.data.last_name || "",
        phone: r.data.phone || "", license_number: r.data.license_number || "",
        address_line1: r.data.address_line1 || "", address_line2: r.data.address_line2 || "",
        city: r.data.city || "", province: r.data.province || "", postal_code: r.data.postal_code || "",
      });
    }).catch(e => toast.error(formatApiError(e)));
  }, []);

  const saveProfile = async (e) => {
    e.preventDefault(); setSavingProfile(true);
    try { await api.put("/vet/profile", profile); toast.success("Profile updated"); }
    catch (err) { toast.error(formatApiError(err)); } finally { setSavingProfile(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.new_pw !== pw.confirm) { toast.error("Passwords do not match"); return; }
    if (pw.new_pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setSavingPw(true);
    try {
      await api.put("/vet/change-password", { current_password: pw.current, new_password: pw.new_pw });
      toast.success("Password updated"); setPw({ current: "", new_pw: "", confirm: "" });
    } catch (err) { toast.error(formatApiError(err)); } finally { setSavingPw(false); }
  };

  const saveSignature = async (e) => {
    e.preventDefault();
    if (!sig) { toast.error("Draw your signature first"); return; }
    setSavingSig(true);
    try {
      await api.post("/vet/signature", { signature: sig });
      toast.success("Signature saved. It is applied automatically to every approval");
      setMe(m => ({ ...m, has_signature: true })); setSig("");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSavingSig(false); }
  };

  const inp = "w-full p-3 rounded-xl bg-white/5 border border-white/10 text-sm font-manrope focus:outline-none focus:border-emerald-400";

  return (
    <div className="px-3 sm:px-6 lg:px-10 py-5 sm:py-8 lg:py-10 max-w-2xl" data-testid="vet-settings">
      <div className="mb-8">
        <p className="font-manrope text-xs uppercase tracking-widest text-emerald-400/80 mb-2">Vet Portal</p>
        <h1 className="font-outfit font-bold text-2xl sm:text-3xl tracking-tight">Settings</h1>
      </div>
      <div className="flex gap-1 mb-6 border-b border-white/10">
        {[{ id: "profile", label: "Profile & Security", Icon: User }, { id: "signature", label: "Signature", Icon: PenTool }].map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-manrope font-medium border-b-2 transition-colors ${activeTab === id ? "border-emerald-500 text-emerald-400" : "border-transparent text-white/40 hover:text-white"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {activeTab === "profile" && (
        <div className="space-y-6">
          <form onSubmit={saveProfile} className="bg-[#142318] border border-white/5 rounded-2xl p-6 space-y-4">
            <h2 className="font-manrope font-semibold text-sm uppercase tracking-wider text-white/60 mb-2">Profile Information</h2>
            <div className="grid grid-cols-2 gap-4">
              <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">First Name</label>
                <input value={profile.first_name} onChange={e => setProfile(p => ({ ...p, first_name: e.target.value }))} className={inp} /></div>
              <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">Last Name</label>
                <input value={profile.last_name} onChange={e => setProfile(p => ({ ...p, last_name: e.target.value }))} className={inp} /></div>
            </div>
            <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">Phone</label>
              <input value={profile.phone} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} className={inp} /></div>
            <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">License Number</label>
              <input value={profile.license_number} onChange={e => setProfile(p => ({ ...p, license_number: e.target.value }))} className={inp} /></div>
            <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">Address Line 1</label>
              <input value={profile.address_line1} onChange={e => setProfile(p => ({ ...p, address_line1: e.target.value }))} className={inp} /></div>
            <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">Address Line 2</label>
              <input value={profile.address_line2} onChange={e => setProfile(p => ({ ...p, address_line2: e.target.value }))} className={inp} /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">City</label>
                <input value={profile.city} onChange={e => setProfile(p => ({ ...p, city: e.target.value }))} className={inp} /></div>
              <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">Province</label>
                <input value={profile.province} onChange={e => setProfile(p => ({ ...p, province: e.target.value }))} className={inp} /></div>
              <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">Postal Code</label>
                <input value={profile.postal_code} onChange={e => setProfile(p => ({ ...p, postal_code: e.target.value }))} className={inp} /></div>
            </div>
            <button type="submit" disabled={savingProfile} className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0e1812] rounded-xl font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
              {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Profile
            </button>
          </form>
          <form onSubmit={savePassword} className="bg-[#142318] border border-white/5 rounded-2xl p-6 space-y-4">
            <h2 className="font-manrope font-semibold text-sm uppercase tracking-wider text-white/60 mb-2">Change Password</h2>
            <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">Current Password</label>
              <input type="password" autoComplete="off" value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} className={inp} /></div>
            <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">New Password</label>
              <input type="password" autoComplete="new-password" value={pw.new_pw} onChange={e => setPw(p => ({ ...p, new_pw: e.target.value }))} className={inp} /></div>
            <div><label className="font-manrope text-xs text-white/60 uppercase tracking-wider mb-2 block">Confirm New Password</label>
              <input type="password" autoComplete="new-password" value={pw.confirm} onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} className={inp} /></div>
            <button type="submit" disabled={savingPw} className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0e1812] rounded-xl font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors">
              {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Update Password
            </button>
          </form>
        </div>
      )}

      {activeTab === "signature" && (
        <div className="bg-[#142318] border border-white/5 rounded-2xl p-6 space-y-4">
          <div>
            <h2 className="font-manrope font-semibold text-sm uppercase tracking-wider text-white/60">Your Signature</h2>
            {me?.has_signature && (
              <div className="flex items-center gap-1.5 mt-2 text-xs text-emerald-400 font-manrope">
                <CheckCircle className="w-3.5 h-3.5" /> Signature saved. It is applied automatically to every prescription you approve.
              </div>
            )}
          </div>
          <p className="font-manrope text-sm text-white/50">Draw once. PawLinx auto-applies it every time you approve, so there is no drawing each time.</p>
          <form onSubmit={saveSignature} className="space-y-4">
            <SignaturePad onChange={setSig} />
            <button type="submit" disabled={savingSig || !sig}
              className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-[#0e1812] rounded-xl font-manrope font-semibold text-sm flex items-center justify-center gap-2 transition-colors" data-testid="vet-save-signature">
              {savingSig ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> {me?.has_signature ? "Update Signature" : "Save Signature"}</>}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
